import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react"
import { createPortal } from "react-dom"
import { SCENE_3D_STAGE_ATTR } from "@/lib/dialog-layer"
import {
  placeInBounds,
  PLACE_IN_BOUNDS_GAP_PX,
  SCENE_STAGE_EDGE_PADS,
  type PlaceAlign,
  type PlaceEdgePads,
  type PlaceRect,
  type PlaceSide,
} from "@/lib/location-scene-3d/place-in-bounds"
import { cn } from "@/lib/utils"

export function sceneStageElement(): HTMLElement | null {
  if (typeof document === "undefined") return null
  const node = document.querySelector(`[${SCENE_3D_STAGE_ATTR}]`)
  return node instanceof HTMLElement ? node : null
}

export type StageAnchorBox = {
  x: number
  y: number
  width?: number
  height?: number
}

type StageAnchoredProps = {
  /** Stage element. Falls back to `[data-scene-3d-stage]`. */
  boundsRef?: RefObject<HTMLElement | null>
  /** Button box, converted into stage coordinates. Wins over `anchorBox`. */
  anchorRef?: RefObject<HTMLElement | null>
  /** Anchor already in stage coordinates. */
  anchorBox?: StageAnchorBox | null
  prefer: PlaceSide
  align?: PlaceAlign
  gap?: number
  /** One number applies to every side. Default clears the toolbar and timeline. */
  pad?: number | PlaceEdgePads
  open?: boolean
  className?: string
  children: ReactNode
  /** Portaled panel, so an outside-click check can include it. */
  panelRef?: RefObject<HTMLDivElement | null>
}

function samePlace(prev: { left: number; top: number } | null, next: { left: number; top: number }): boolean {
  if (!prev) return false
  return Math.abs(prev.left - next.left) < 0.5 && Math.abs(prev.top - next.top) < 0.5
}

function readAnchor(
  bounds: HTMLElement,
  anchorRef: RefObject<HTMLElement | null> | undefined,
  box: StageAnchorBox | null | undefined,
): PlaceRect | null {
  const el = anchorRef?.current
  if (el) {
    const anchorRect = el.getBoundingClientRect()
    const boundsRect = bounds.getBoundingClientRect()
    return {
      x: anchorRect.left - boundsRect.left,
      y: anchorRect.top - boundsRect.top,
      width: anchorRect.width,
      height: anchorRect.height,
    }
  }
  if (!box) return null
  return { x: box.x, y: box.y, width: box.width ?? 0, height: box.height ?? 0 }
}

/**
 * Portal a panel into the 3D stage and keep it inside the stage rectangle.
 * Hidden until the first measurement so it does not flash on the wrong side.
 */
export function StageAnchored({
  boundsRef,
  anchorRef,
  anchorBox,
  prefer,
  align = "start",
  gap = PLACE_IN_BOUNDS_GAP_PX,
  pad = SCENE_STAGE_EDGE_PADS,
  open = true,
  className,
  children,
  panelRef,
}: StageAnchoredProps) {
  const innerRef = useRef<HTMLDivElement | null>(null)
  const [boundsEl, setBoundsEl] = useState<HTMLElement | null>(null)
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null)
  const placeRef = useRef(place)
  placeRef.current = place

  const setNode = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node
      if (panelRef) panelRef.current = node
    },
    [panelRef],
  )

  useEffect(() => {
    return () => {
      if (panelRef) panelRef.current = null
    }
  }, [panelRef])

  const ax = anchorBox?.x
  const ay = anchorBox?.y
  const aw = anchorBox?.width ?? 0
  const ah = anchorBox?.height ?? 0

  const update = useCallback(() => {
    const bounds = boundsEl
    const panel = innerRef.current
    if (!bounds || !panel) return
    const box = ax == null || ay == null ? null : { x: ax, y: ay, width: aw, height: ah }
    const anchor = readAnchor(bounds, anchorRef, box)
    if (!anchor) return
    const size = { width: panel.offsetWidth, height: panel.offsetHeight }
    if (size.width <= 0 || size.height <= 0) return
    const boundsRect = bounds.getBoundingClientRect()
    const next = placeInBounds({
      anchor,
      size,
      bounds: { width: boundsRect.width, height: boundsRect.height },
      prefer,
      align,
      gap,
      pad,
    })
    if (samePlace(placeRef.current, next)) return
    setPlace({ left: next.left, top: next.top })
  }, [align, anchorRef, aw, ah, ax, ay, boundsEl, gap, pad, prefer])

  const updateRef = useRef(update)
  updateRef.current = update

  useLayoutEffect(() => {
    if (!open) {
      setBoundsEl(null)
      setPlace(null)
      return
    }
    const next = boundsRef?.current ?? sceneStageElement()
    setBoundsEl((prev) => (prev === next ? prev : next))
  }, [boundsRef, open])

  useLayoutEffect(() => {
    if (!open || !boundsEl) return
    updateRef.current()
  }, [boundsEl, open, update])

  useLayoutEffect(() => {
    if (!open || !boundsEl) return
    const run = () => updateRef.current()
    const ro = new ResizeObserver(run)
    ro.observe(boundsEl)
    if (innerRef.current) ro.observe(innerRef.current)
    window.addEventListener("resize", run)
    window.addEventListener("scroll", run, true)
    // Button anchors move when a parent chrome is dragged or the camera shifts
    // the bar, without this component's props changing.
    let raf = 0
    if (anchorRef) {
      const loop = () => {
        run()
        raf = requestAnimationFrame(loop)
      }
      raf = requestAnimationFrame(loop)
    }
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener("resize", run)
      window.removeEventListener("scroll", run, true)
    }
  }, [anchorRef, boundsEl, open])

  if (!open || !boundsEl) return null

  return createPortal(
    <div
      ref={setNode}
      className={cn("pointer-events-auto absolute z-40", className)}
      style={{
        left: place?.left ?? 0,
        top: place?.top ?? 0,
        visibility: place ? "visible" : "hidden",
        pointerEvents: place ? "auto" : "none",
      }}
    >
      {children}
    </div>,
    boundsEl,
  )
}
